import { DEFAULT_FUNDING_MODE, FUNDING_MODES, type FundingMode } from '@paguz/contracts';
import {
  Badge,
  cn,
  formatCop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { ArrowRightIcon, CheckCircle2Icon, ScaleIcon } from 'lucide-react';
import { Field, Select, Switch } from '../../components/form-controls.tsx';
import { DataList, Panel } from '../../components/page.tsx';
import { formatBusinessDate, formatRate } from '../../lib/format.ts';
import { FUNDING_MODE, SAGA_STEP } from '../../lib/labels.ts';
import {
  FormCard,
  IntField,
  MoneyField,
  RateField,
  SettingSection,
  useUsury,
} from './setting-editor.tsx';

// ── Fondeo y GMF ──────────────────────────────────────────────────────────
const FUNDING_LABELS: Readonly<Record<string, string>> = {
  mode: 'Modo de fondeo',
  'gmf.SETTLEMENT_ACCOUNT.enabled': 'GMF en cuenta puente',
  'gmf.SETTLEMENT_ACCOUNT.rate': 'Tasa GMF (cuenta puente)',
  'gmf.LINKED_ACCOUNT_JIT.enabled': 'GMF en cuenta vinculada',
  'gmf.LINKED_ACCOUNT_JIT.rate': 'Tasa GMF (cuenta vinculada)',
  brebConfirmationTimeoutSeconds: 'SLA de confirmación Bre-B',
  authorizationTtlMinutes: 'Vencimiento de la autorización',
};

const MODE_STEPS: Readonly<Record<FundingMode, readonly (keyof typeof SAGA_STEP)[]>> = {
  SETTLEMENT_ACCOUNT: ['AUTHORIZE', 'SEND', 'AWAIT_CONFIRMATION', 'CAPTURE'],
  LINKED_ACCOUNT_JIT: ['AUTHORIZE', 'FUND', 'SEND', 'AWAIT_CONFIRMATION', 'CAPTURE', 'DEBIT'],
};

export function FundingSection() {
  return (
    <SettingSection
      settingKey="funding"
      title="Modo de fondeo y GMF"
      description="De dónde sale el dinero que se paga por Bre-B. Ambos modos usan la misma saga; cambiarlo aplica a las compras nuevas."
      labels={FUNDING_LABELS}
    >
      {({ draft, set, error, canEdit }) => (
        <>
          <FormCard title="Modo de fondeo">
            <div
              role="radiogroup"
              aria-label="Modo de fondeo"
              className="grid gap-4 lg:grid-cols-2"
            >
              {FUNDING_MODES.map((mode) => {
                const selected = draft.mode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={!canEdit}
                    onClick={() => {
                      set(['mode'], mode);
                    }}
                    className={cn(
                      'flex cursor-pointer flex-col gap-3 rounded-2xl border-2 p-5 text-left transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none disabled:cursor-default',
                      selected
                        ? 'border-highlight bg-highlight-soft/40'
                        : 'border-border hover:border-highlight/40',
                    )}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-display text-lg font-semibold text-foreground">
                        {FUNDING_MODE[mode].label}
                      </span>
                      <span className="flex items-center gap-2">
                        {mode === DEFAULT_FUNDING_MODE && (
                          <Badge variant="secondary">Por defecto</Badge>
                        )}
                        {selected && <CheckCircle2Icon className="size-5 text-highlight" />}
                      </span>
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {FUNDING_MODE[mode].description}
                    </span>
                    <span className="flex flex-wrap items-center gap-1 text-xs">
                      {MODE_STEPS[mode].map((step, i) => (
                        <span key={step} className="flex items-center gap-1">
                          {i > 0 && <ArrowRightIcon className="size-3 text-muted-foreground" />}
                          <span
                            className={cn(
                              'rounded-md px-1.5 py-0.5 font-semibold',
                              step === 'FUND' || step === 'DEBIT'
                                ? 'bg-warning-soft text-warning-soft-foreground'
                                : 'bg-muted text-foreground',
                            )}
                          >
                            {SAGA_STEP[step].label}
                          </span>
                        </span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
            {error(['mode']) && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error(['mode'])}
              </p>
            )}
            {draft.settlementAccount && (
              <div className="rounded-xl border border-dashed border-border p-4">
                <p className="mb-3 text-sm font-semibold">Cuenta puente de la entidad</p>
                <DataList
                  columns={4}
                  items={[
                    { label: 'Titular', value: draft.settlementAccount.holderName },
                    { label: 'Participante', value: draft.settlementAccount.participantCode },
                    {
                      label: 'Tipo',
                      value:
                        draft.settlementAccount.accountType === 'CHECKING'
                          ? 'Corriente'
                          : 'Ahorros',
                    },
                    {
                      label: 'Número',
                      value: `****${draft.settlementAccount.accountNumber.slice(-4)}`,
                    },
                  ]}
                />
              </div>
            )}
          </FormCard>

          <FormCard
            title="GMF (4 × 1.000) por modo"
            description="Gravamen a los movimientos financieros: tasa y si aplica en cada modo. Desactivado en el demo."
          >
            <div className="grid gap-4 lg:grid-cols-2">
              {FUNDING_MODES.map((mode) => (
                <div key={mode} className="flex flex-col gap-4 rounded-xl border border-border p-4">
                  <Switch
                    label={`Aplica en ${FUNDING_MODE[mode].label.toLowerCase()}`}
                    checked={draft.gmf[mode].enabled}
                    onCheckedChange={(v) => {
                      set(['gmf', mode, 'enabled'], v);
                    }}
                  />
                  <RateField
                    label="Tasa"
                    value={draft.gmf[mode].rate}
                    onChange={(v) => {
                      set(['gmf', mode, 'rate'], v);
                    }}
                    error={error(['gmf', mode, 'rate'])}
                    hint={`${formatRate(draft.gmf[mode].rate, '', 2)} = ${formatCop(rateTimes(draft.gmf[mode].rate, 1000))} por cada $ 1.000`}
                  />
                </div>
              ))}
            </div>
          </FormCard>

          <FormCard title="Tiempos de la saga">
            <div className="grid gap-5 md:grid-cols-2">
              <IntField
                label="SLA de confirmación Bre-B"
                suffix="segundos"
                value={draft.brebConfirmationTimeoutSeconds}
                onChange={(v) => {
                  set(['brebConfirmationTimeoutSeconds'], v);
                }}
                error={error(['brebConfirmationTimeoutSeconds'])}
                hint="Sin callback en este tiempo, la compra pasa a «en verificación»."
              />
              <IntField
                label="Vencimiento de la autorización (hold)"
                suffix="minutos"
                value={draft.authorizationTtlMinutes}
                onChange={(v) => {
                  set(['authorizationTtlMinutes'], v);
                }}
                error={error(['authorizationTtlMinutes'])}
              />
            </div>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}

/** `0.004000` × 1000 → `"4"` (ejemplo informativo del GMF; no es una cifra de crédito). */
function rateTimes(rate: string, base: number): string {
  const [int = '0', dec = ''] = rate.split('.');
  const scaled = `${int}${dec.padEnd(6, '0')}`;
  const value = (BigInt(scaled) * BigInt(base)) / 1_000_000n;
  return value.toString();
}

// ── Producto: parámetros financieros (P1) ─────────────────────────────────
const PRODUCT_LABELS: Readonly<Record<string, string>> = {
  rateEA: 'Tasa EA',
  moratoriumRateEA: 'Tasa moratoria',
  'mipymeFee.rate': 'Comisión MiPyme',
  'lifeInsurance.monthlyRate': 'Seguro de vida (mensual)',
  limitMin: 'Cupo mínimo',
  limitMax: 'Cupo máximo',
  purchaseMin: 'Compra mínima',
  allowedInstallments: 'Cuotas permitidas',
  'billing.cutDay': 'Día de corte',
  'billing.dueDays': 'Días a fecha límite',
  graceDays: 'Días de gracia',
  authorizationTtlMinutes: 'Vencimiento de la autorización',
};

const INSTALLMENT_OPTIONS = Array.from({ length: 36 }, (_, i) => i + 1);

export function ProductSection() {
  const { usury, rates } = useUsury();
  return (
    <SettingSection
      settingKey="product"
      title="Producto y tasas"
      description="Parámetros financieros del Microcrédito Rotativo. Las tasas se validan contra la usura vigente; los cambios aplican a nuevas utilizaciones."
      labels={PRODUCT_LABELS}
      aside={() => (
        <Panel title="Tasas de usura" description="Certificadas por periodo (valores de ejemplo).">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modalidad</TableHead>
                <TableHead>Vigencia</TableHead>
                <TableHead className="text-right">Tasa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map((r) => (
                <TableRow
                  key={`${r.modality}-${r.validFrom}`}
                  className={cn(usury === r && 'bg-highlight-soft/40')}
                >
                  <TableCell className="text-xs">
                    {r.modality.replace(/_/g, ' ').toLowerCase()}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {formatBusinessDate(r.validFrom)} – {formatBusinessDate(r.validTo)}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{formatRate(r.rateEA)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
    >
      {({ draft, set, error }) => (
        <>
          <FormCard
            title="Tasas"
            actions={
              usury && (
                <Badge variant="highlight">
                  <ScaleIcon /> Usura vigente {formatRate(usury.rateEA)}
                </Badge>
              )
            }
          >
            <div className="grid gap-5 md:grid-cols-2">
              <RateField
                label="Tasa efectiva anual (EA)"
                value={draft.rateEA}
                onChange={(v) => {
                  set(['rateEA'], v);
                }}
                error={error(['rateEA'])}
                suffix="% EA"
                hint="Debe ser ≤ tasa de usura vigente de la modalidad."
              />
              <Field label="Tasa moratoria" htmlFor="mora-mode">
                <Select
                  id="mora-mode"
                  value={draft.moratoriumRateEA === 'USURY' ? 'USURY' : 'CUSTOM'}
                  onChange={(e) => {
                    set(['moratoriumRateEA'], e.target.value === 'USURY' ? 'USURY' : draft.rateEA);
                  }}
                >
                  <option value="USURY">Igual a la tasa de usura vigente</option>
                  <option value="CUSTOM">Tasa fija</option>
                </Select>
              </Field>
              {draft.moratoriumRateEA !== 'USURY' && (
                <RateField
                  label="Tasa moratoria fija"
                  value={draft.moratoriumRateEA}
                  onChange={(v) => {
                    set(['moratoriumRateEA'], v);
                  }}
                  error={error(['moratoriumRateEA'])}
                  suffix="% EA"
                />
              )}
              <RateField
                label="Comisión MiPyme"
                value={draft.mipymeFee.rate}
                onChange={(v) => {
                  set(['mipymeFee', 'rate'], v);
                }}
                error={error(['mipymeFee', 'rate'])}
                hint="Ley 590/2000. 0 % en el demo."
              />
              <RateField
                label="Seguro de vida deudores (mensual)"
                value={draft.lifeInsurance.monthlyRate}
                onChange={(v) => {
                  set(['lifeInsurance', 'monthlyRate'], v);
                }}
                error={error(['lifeInsurance', 'monthlyRate'])}
                suffix="% MV"
                hint="Sobre el saldo de capital."
              />
            </div>
          </FormCard>

          <FormCard title="Cupos y compras">
            <div className="grid gap-5 md:grid-cols-3">
              <MoneyField
                label="Cupo mínimo"
                value={draft.limitMin}
                onChange={(v) => {
                  set(['limitMin'], v);
                }}
                error={error(['limitMin'])}
              />
              <MoneyField
                label="Cupo máximo"
                value={draft.limitMax}
                onChange={(v) => {
                  set(['limitMax'], v);
                }}
                error={error(['limitMax'])}
              />
              <MoneyField
                label="Compra mínima"
                value={draft.purchaseMin}
                onChange={(v) => {
                  set(['purchaseMin'], v);
                }}
                error={error(['purchaseMin'])}
              />
            </div>
            <Field label="Cuotas permitidas" error={error(['allowedInstallments'])}>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Cuotas permitidas">
                {INSTALLMENT_OPTIONS.map((n) => {
                  const on = draft.allowedInstallments.includes(n);
                  return (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={on}
                      onClick={() => {
                        const next = on
                          ? draft.allowedInstallments.filter((x) => x !== n)
                          : [...draft.allowedInstallments, n].sort((a, b) => a - b);
                        set(['allowedInstallments'], next);
                      }}
                      className={cn(
                        'h-10 min-w-12 cursor-pointer rounded-lg border px-3 text-sm font-semibold tabular-nums transition-colors disabled:cursor-default',
                        on
                          ? 'border-highlight bg-highlight text-highlight-foreground'
                          : 'border-border bg-background text-foreground hover:bg-accent',
                      )}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </Field>
          </FormCard>

          <FormCard title="Facturación">
            <div className="grid gap-5 md:grid-cols-4">
              <IntField
                label="Día de corte"
                suffix="del mes"
                value={draft.billing.cutDay}
                onChange={(v) => {
                  set(['billing', 'cutDay'], v);
                }}
                error={error(['billing', 'cutDay'])}
              />
              <IntField
                label="Días a fecha límite"
                suffix="días"
                value={draft.billing.dueDays}
                onChange={(v) => {
                  set(['billing', 'dueDays'], v);
                }}
                error={error(['billing', 'dueDays'])}
              />
              <IntField
                label="Días de gracia"
                suffix="días"
                value={draft.graceDays}
                onChange={(v) => {
                  set(['graceDays'], v);
                }}
                error={error(['graceDays'])}
              />
              <IntField
                label="Vencimiento del hold"
                suffix="min"
                value={draft.authorizationTtlMinutes}
                onChange={(v) => {
                  set(['authorizationTtlMinutes'], v);
                }}
                error={error(['authorizationTtlMinutes'])}
              />
            </div>
          </FormCard>
        </>
      )}
    </SettingSection>
  );
}
